'use client';

import React, { useEffect, useState, type ReactElement } from 'react';
import { Drawer, Heading, SkeletonBar, TextLink, TextPassage } from '@petsmart-ui/sparky';
import {
    AccountDrawerType,
    logSentryError,
    useLoyaltyContext,
    useOptimizely,
    usePageLayout,
    useUserSession,
    SentryAcpTreatsRewardsErrors as SentryErrors,
    useAlertBanner,
} from '@libs/common-next';
import { Offer } from '@libs/common-types';
import { getCardSize, useOfferCardActions } from './useOfferCardActions';
import { AlertBannerContainer } from '../ValidationAlert';
import { OfferCard } from './OfferCard';
import './OffersDrawer.scss';
import { usePathname } from 'next/navigation';

export interface OffersProps {
    gtmSection?: string;
}
/**
 * AB Testing Component AC-4466
 * Temporary testing component. Will optimizely depending on AB test results
 * Will most likely combine with OfferCardDrawer
 * libs/shared-ui/src/components/OfferCard/OfferCardDrawer.tsx
 * (possibly CPC OfferDrawer)
 */
export function OffersDrawer(): ReactElement | null {
    const { setAlert } = useAlertBanner();
    const { accountDrawersOpen, removeAccountDrawers, openAccountDrawer, userData, guestOffers } = usePageLayout();
    const isActive = accountDrawersOpen?.includes(AccountDrawerType.OFFERS);
    const { offers } = useLoyaltyContext();
    const { isAuthenticated, user } = useUserSession();
    const {
        drawerOpen,
        selectedOffer,
        handleActivateOffer,
        isActivating,
        setSelectedOffer,
        handleCloseDrawer,
        setDrawerOpen,
    } = useOfferCardActions();
    const [isLoading, setIsLoading] = useState(false);
    const [offersList, setOffersList] = useState<Offer[]>([]);
    const [offersListGuest, setOffersListGuest] = useState<Offer[]>([]);
    const [isOfferActivating, setIsOfferActivating] = useState(false);
    const componentClassName = 'offers-drawer-list';
    const { context } = useOptimizely();
    const pathname = usePathname();
    const OffersDrawerSkeleton = () => {
        return (
            <div>
                {['large', 'large', 'large', 'small', 'small', 'small'].map((size, pos) => {
                    const key = `${size}-${pos + 1}`;
                    return (
                        <SkeletonBar key={key} className={`${componentClassName}__loader card__${size}`}>
                            <div className={`${componentClassName}__loader__header`}>
                                <SkeletonBar className={`${componentClassName}__loader__image`} />
                                <SkeletonBar className={`${componentClassName}__loader__title`} />
                                {size === 'large' && <SkeletonBar className={`${componentClassName}__loader__desc`} />}
                            </div>
                            <div className={`${componentClassName}__loader__footer`}>
                                <SkeletonBar className={`${componentClassName}__loader__exp`} />
                                <SkeletonBar className={`${componentClassName}__loader__button`} />
                            </div>
                        </SkeletonBar>
                    );
                })}
            </div>
        );
    };
    async function getContentfulOffers() {
        if (guestOffers) {
            const mappedOffers = guestOffers;
            mappedOffers?.sort((offer1, offer2) => {
                const off1 = offer1.displayRank ?? 999;
                const off2 = offer2.displayRank ?? 999;

                if (off1 !== off2) return off1 - off2;

                const offer1name = offer1.name?.toLowerCase() ?? '';
                const offer2name = offer2.name?.toLowerCase() ?? '';

                return offer1name.localeCompare(offer2name);
            });
            setOffersListGuest(mappedOffers);
            setIsLoading(false);
        }
    }

    function formatOffers(unorderedOffers: Offer[]) {
        const largeOffers = unorderedOffers
            .reduce((result: Offer[], offer: Offer) => {
                if (offer.cardSize === 'Large') result.push(offer);
                return result;
            }, [])
            .slice(0, 5);
        const smallOffers = unorderedOffers
            .reduce((result: Offer[], offer: Offer) => {
                if (offer.cardSize === 'Small') result.push(offer);
                return result;
            }, [])
            .slice(0, 5);
        const filteredOffers = largeOffers.concat(smallOffers);
        setOffersList(filteredOffers);
    }

    function activateOffer(offer: Offer) {
        setIsOfferActivating(true);
        handleActivateOffer(offer)
            .catch(e => {
                window.localStorage.removeItem('opt-ex-offer-id');

                logSentryError(e, SentryErrors.OfferCard.ActivateOfferFailed);
            })
            .finally(() => {
                if (offers) formatOffers(offers);
                if (!pathname?.includes('/sign-in')) window.localStorage.removeItem('opt-ex-offer-id');
                setIsLoading(false);
                setIsOfferActivating(false);
            });
    }

    function matchOffer(offerId: string | null | boolean) {
        if (offers) {
            const foundObjectById = offers?.find(item => item.offerId === offerId);
            if (!foundObjectById) {
                if (!drawerOpen) {
                    handleCloseDrawer();
                    setDrawerOpen(false);
                }
                setIsLoading(false);
                formatOffers(offers);
                setAlert({
                    header: 'Uh oh!',
                    text: `Offer was not found in your account. Please try another offer.`,
                    variant: 'error',
                    icon: 'exclamation-circle-fill',
                });

                window.localStorage.removeItem('opt-ex-offer-id');

                logSentryError(
                    new Error(`Failed to activate an offer, offerId: ${offerId}`),
                    SentryErrors.OfferCard.ActivateOfferFailed
                );
            }
            if (foundObjectById && !isOfferActivating && !foundObjectById.isActivated) {
                activateOffer(foundObjectById);
            }
            if (foundObjectById?.isActivated) {
                formatOffers(offers);
                setSelectedOffer(foundObjectById);
                setIsLoading(false);
                if (!pathname?.includes('/sign-in')) window.localStorage.removeItem('opt-ex-offer-id');
            }
        }
    }

    useEffect(() => {
        if (guestOffers && guestOffers.length > 0) {
            getContentfulOffers();
        }
    }, [guestOffers]);

    useEffect(() => {
        const offerId = window.localStorage.getItem('opt-ex-offer-id');
        if (user && offerId && !isActivating) {
            setIsLoading(true);

            openAccountDrawer(AccountDrawerType.OFFERS);
            if (offers && offers?.length > 0) {
                setIsLoading(false);
                formatOffers(offers);
                matchOffer(offerId);
            }
        }
    }, [user, userData?.profile?.firstName]);

    useEffect(() => {
        if (offers) {
            const offerId = window.localStorage.getItem('opt-ex-offer-id');
            if ((isAuthenticated || user) && offers.length > 0) {
                if (!offerId) {
                    // if logged in and has offers, set filtered offers
                    formatOffers(offers);
                    setIsLoading(false);
                } else {
                    matchOffer(offerId);
                }
            }
        }
    }, [offers]);

    useEffect(() => {
        if (offersListGuest && !user) {
            if (offersListGuest.length > 0) {
                setIsLoading(true);
                // if logged in and has offers, set filtered offers
                formatOffers(offersListGuest);
                setIsLoading(false);
            }
        }
    }, [offersListGuest]);

    return (
        <Drawer
            align="right"
            className="c-offer-card-drawer opti-offers offers-drawer-list"
            data-testid="offers-drawer"
            dismissible
            isActive={isActive}
            onClose={() => {
                window.localStorage.removeItem('opt-ex-offer-id');
                if (!drawerOpen) {
                    context?.trackEvent('exit_click');
                    removeAccountDrawers([AccountDrawerType.OFFERS]);
                }
            }}
        >
            <Drawer.Header>
                <Heading tagName="h2" size="headline">
                    Treats Rewards offers
                </Heading>
            </Drawer.Header>
            <Drawer.Body>
                {isLoading ? (
                    <OffersDrawerSkeleton />
                ) : (
                    <div className={`${componentClassName}__grid`}>
                        <AlertBannerContainer data-testid="error-banner" />
                        {offersList.map(row => {
                            const rowKey = row.offerId;
                            return (
                                // OPTI-TODO: split into two and display large first then small (5 each)
                                <React.Fragment key={rowKey}>
                                    {row.offerId === selectedOffer?.offerId && !drawerOpen && (
                                        <AlertBannerContainer data-testid="error-banner" />
                                    )}
                                    <div className={`${componentClassName}__grid__item`}>
                                        {
                                            <OfferCard
                                                key={row.offerId}
                                                offer={row}
                                                size={
                                                    getCardSize(row).includes('large') ? 'featured' : getCardSize(row)
                                                }
                                            />
                                        }
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
            </Drawer.Body>
            <Drawer.Footer>
                <TextPassage className="offer-card-drawer-link" capLinelength={false}>
                    {isAuthenticated ? (
                        <TextLink
                            href="/treats-rewards/authenticated/offers"
                            onClick={() => {
                                context?.trackEvent('see_all_offers');
                            }}
                            screenReaderText="See all offers"
                        >
                            See all offers
                        </TextLink>
                    ) : (
                        <TextLink
                            onClick={() => {
                                context?.trackEvent('see_all_offers');
                                openAccountDrawer(AccountDrawerType.SIGN_IN);
                            }}
                            screenReaderText="See all offers"
                        >
                            See all offers
                        </TextLink>
                    )}
                </TextPassage>
            </Drawer.Footer>
        </Drawer>
    );
}
